/**
 * Safe number parsing utilities to prevent NaN errors
 * Handles empty strings, null, undefined, and invalid number formats
 */

/**
 * Safely parse an integer value, returning fallback for invalid numbers
 * @param value - Value to parse (string or number)
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @param radix - Radix for parsing (default: 10)
 * @returns Parsed integer or fallback value
 */
export function safeParseInt(
  value: string | number | null | undefined,
  fallback: number = 0,
  radix: number = 10
): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? fallback : Math.floor(value);
  }

  const parsed = Number.parseInt(value, radix);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Safely parse an integer value that may be optional
 * Returns undefined when parsing fails instead of a fallback number
 */
export function safeParseOptionalInt(
  value: string | number | null | undefined,
  radix: number = 10
): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? undefined : Math.floor(value);
  }

  const parsed = Number.parseInt(value, radix);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Safely parse a float value, returning fallback for invalid numbers
 * @param value - Value to parse (string or number)
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @returns Parsed float or fallback value
 */
export function safeParseFloat(
  value: string | number | null | undefined,
  fallback: number = 0
): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? fallback : value;
  }

  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Safely parse a float value that may be optional
 * Returns undefined when parsing fails instead of a fallback number
 */
export function safeParseOptionalFloat(
  value: string | number | null | undefined
): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? undefined : value;
  }

  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Check if a value can be parsed as a valid number
 * @param value - Value to check
 * @returns True if value is a valid number
 */
export function isValidNumber(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'number') {
    return !isNaN(value) && isFinite(value);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return !isNaN(parsed) && isFinite(parsed);
  }

  return false;
}

/**
 * Sanitize number field values, converting invalid values to null
 * @param value - Value to sanitize
 * @returns Number or null
 */
export function sanitizeNumberField(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Clamp a number between min and max values
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Safely parse a percentage string (e.g., "50%") to a decimal (0.5)
 * @param value - Percentage string or number
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @returns Decimal value or fallback
 */
export function safeParsePercentage(
  value: string | number | null | undefined,
  fallback: number = 0
): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? fallback : value;
  }

  // Remove % sign if present
  const cleanValue = value.replace('%', '').trim();
  const parsed = Number.parseFloat(cleanValue);

  if (isNaN(parsed)) {
    return fallback;
  }

  // If original value had %, convert to decimal
  return value.includes('%') ? parsed / 100 : parsed;
}
